import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, Loader2, Star } from 'lucide-react';
import { Switch } from '../ui/switch';
import { API, getAuthHeaders } from '../../utils/adminAuth';

export default function FeedbackModal({ feedback, onClose, onSave }) {
  const isEdit = !!feedback;
  const [form, setForm] = useState({
    customer_name: feedback?.customer_name || '',
    comment: feedback?.comment || '',
    rating: feedback?.rating ?? 5,
    image_url: feedback?.image_url || '',
    is_approved: feedback?.is_approved ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload/product-image`, fd, { headers: getAuthHeaders() });
      const imageUrl = `${API}/files/${res.data.storage_path}`;
      setForm(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Photo uploaded');
    } catch { toast.error('Upload failed'); }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file?.type?.startsWith('image/')) handleImageUpload(file);
  };

  const handleSave = async () => {
    if (!form.customer_name.trim() || !form.comment.trim()) { toast.error('Name and comment required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/feedback/${feedback.id}`, form, { headers: getAuthHeaders() });
        toast.success('Feedback updated');
      } else {
        await axios.post(`${API}/admin/feedback`, form, { headers: getAuthHeaders() });
        toast.success('Feedback added');
      }
      onSave();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="feedback-modal">
      <div className="bg-[#141414] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {isEdit ? 'Edit Feedback' : 'Add Customer Feedback'}
          </h2>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <input type="text" placeholder="Customer name" value={form.customer_name}
            onChange={e => setForm({ ...form, customer_name: e.target.value })}
            className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30"
            data-testid="feedback-modal-name" />

          <textarea placeholder="Customer feedback..." value={form.comment}
            onChange={e => setForm({ ...form, comment: e.target.value })} rows={3}
            className="w-full bg-transparent border-b border-white/20 text-white py-2 text-sm focus:border-[#D4AF37] focus:outline-none placeholder:text-white/30 resize-none"
            data-testid="feedback-modal-comment" />

          <div>
            <p className="text-white/40 text-xs uppercase tracking-wider mb-2 font-sans">Rating</p>
            <div className="flex gap-1">
              {[1,2,3,4,5].map(i => (
                <button key={i} type="button" onClick={() => setForm({ ...form, rating: i })}
                  className="hover:scale-110 transition-transform">
                  <Star className={`w-6 h-6 ${i <= form.rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-white/20'} transition-colors`} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-white/40 text-xs uppercase tracking-wider mb-2 font-sans">Customer Photo</p>
            <div
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
              onClick={() => document.getElementById('fb-img-input')?.click()}
              className="border border-dashed border-white/20 hover:border-[#D4AF37]/50 p-4 text-center cursor-pointer transition-colors"
              data-testid="feedback-image-dropzone"
            >
              {form.image_url ? (
                <img src={form.image_url} alt="Preview" className="w-full h-32 object-cover mb-2" />
              ) : uploading ? (
                <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin mx-auto" />
              ) : (
                <div>
                  <Upload className="w-6 h-6 text-white/20 mx-auto mb-1" />
                  <p className="text-white/30 text-xs font-sans">Drop photo or click to upload</p>
                </div>
              )}
              <input id="fb-img-input" type="file" accept="image/*" className="hidden"
                onChange={e => handleImageUpload(e.target.files?.[0])} />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Switch checked={form.is_approved} onCheckedChange={v => setForm({ ...form, is_approved: v })} data-testid="feedback-approve-toggle" />
            <span className="text-white/50 text-xs font-sans">{form.is_approved ? 'Approved (visible on website)' : 'Pending (hidden from website)'}</span>
          </div>
        </div>

        <button onClick={handleSave} disabled={saving}
          className="w-full bg-[#D4AF37] text-black py-2.5 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 mt-5"
          data-testid="save-feedback-btn">
          {saving ? 'Saving...' : isEdit ? 'Update Feedback' : 'Add Feedback'}
        </button>
      </div>
    </div>
  );
}
